import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertCircle, CheckCircle, XCircle, Scale, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 mb-6 font-medium"
        >
          <ArrowLeft size={20} />
          Back to Login
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText size={32} className="text-blue-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
              <p className="text-slate-600">Last updated: December 13, 2024</p>
            </div>
          </div>
          
          <p className="text-slate-700 leading-relaxed">
            Welcome to We Are Pilgrims. By accessing or using our virtual primary care platform, you agree to be bound by these Terms of Service. Please read them carefully.
          </p>

          <Alert className="mt-4 bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>First time here?</strong> After reading these terms, simply close this tab to return to the consent screen where you can accept our terms.
            </AlertDescription>
          </Alert>
        </div>

        {/* Content Sections */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          
          {/* Section 1 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">1. Acceptance of Terms</h2>
            </div>
            <div className="space-y-3 text-slate-700 leading-relaxed">
              <p>
                By creating an account and using We Are Pilgrims, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
              <p>
                If you do not agree with any part of these terms, you may not access or use our services.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">2. Services Provided</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p><strong>We Are Pilgrims provides:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Virtual primary care consultations with licensed healthcare providers</li>
                <li>Electronic health records management</li>
                <li>Health monitoring and goal tracking</li>
                <li>Secure communication with your care team</li>
                <li>Membership-based healthcare services</li>
              </ul>
              
              <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
                <p className="flex items-start gap-2">
                  <AlertCircle size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Important:</strong> Our services are not for medical emergencies. 
                    In case of emergency, call 911 or your local emergency number immediately.
                  </span>
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">3. Eligibility</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>To use our services, you must:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Be at least 18 years of age, or have parental/guardian consent</li>
                <li>Be a resident of the Philippines</li>
                <li>Provide accurate and complete information during registration</li>
                <li>Have a valid email address and mobile phone number</li>
                <li>Maintain an active membership subscription</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">4. Account Responsibilities</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p><strong>You are responsible for:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
                <li>Providing accurate and up-to-date health information</li>
                <li>Following treatment plans and provider recommendations</li>
              </ul>
              
              <p className="mt-4"><strong>You agree NOT to:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Share your account credentials with others</li>
                <li>Use another person's account without permission</li>
                <li>Provide false or misleading information</li>
                <li>Use the platform for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with the proper functioning of the platform</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Scale size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">5. Healthcare Provider Relationship</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                <strong>Provider-Patient Relationship:</strong> By using our services, you establish a 
                provider-patient relationship with the licensed healthcare professionals in our network.
              </p>
              
              <p className="mt-3">
                <strong>Professional Judgment:</strong> All medical advice, diagnoses, and treatment plans 
                are provided at the professional discretion of your healthcare provider. We Are Pilgrims 
                does not practice medicine or provide medical advice.
              </p>
              
              <p className="mt-3">
                <strong>Compliance:</strong> You agree to follow your provider's instructions and attend 
                scheduled appointments. Failure to do so may affect your treatment outcomes.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">6. Membership and Payments</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p><strong>Membership Fees:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Membership fees are billed according to your chosen plan</li>
                <li>All fees are non-refundable unless otherwise stated</li>
                <li>You are responsible for all applicable taxes</li>
              </ul>
              
              <p className="mt-4"><strong>Cancellation:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>You may cancel your membership at any time</li>
                <li>Cancellation takes effect at the end of your current billing cycle</li>
                <li>You will retain access to your health records after cancellation</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <XCircle size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">7. Limitations and Disclaimers</h2>
            </div>
            <div className="space-y-3 text-slate-700 leading-relaxed">
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <p className="font-semibold mb-2">Our services are NOT suitable for:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Medical emergencies (call 911 or go to the nearest hospital)</li>
                  <li>Conditions requiring in-person physical examination</li>
                  <li>Surgical procedures or invasive treatments</li>
                  <li>Controlled substance prescriptions (in most cases)</li>
                </ul>
              </div>
              
              <p className="mt-4">
                <strong>No Guarantees:</strong> While we strive to provide excellent care, we cannot 
                guarantee specific outcomes or results. Healthcare outcomes depend on many factors beyond 
                our control.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Scale size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">8. Liability and Indemnification</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                <strong>Limitation of Liability:</strong> To the maximum extent permitted by law, 
                We Are Pilgrims shall not be liable for any indirect, incidental, special, consequential, 
                or punitive damages arising from your use of our services.
              </p>
              
              <p className="mt-3">
                <strong>Indemnification:</strong> You agree to indemnify and hold We Are Pilgrims, 
                its providers, and staff harmless from any claims arising from:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Your violation of these Terms of Service</li>
                <li>Your violation of any applicable laws or regulations</li>
                <li>Your provision of false or misleading information</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">9. Intellectual Property</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                All content, features, and functionality of the We Are Pilgrims platform, including 
                but not limited to text, graphics, logos, and software, are owned by We Are Pilgrims 
                and protected by copyright and trademark laws.
              </p>
              
              <p className="mt-3">
                <strong>Your Health Data:</strong> You retain all rights to your personal health information. 
                We only use your data as described in our Privacy Policy.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Scale size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">10. Termination</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                We reserve the right to suspend or terminate your account at any time for:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Violation of these Terms of Service</li>
                <li>Fraudulent or illegal activity</li>
                <li>Abusive behavior toward staff or providers</li>
                <li>Non-payment of membership fees</li>
              </ul>
              
              <p className="mt-3">
                Upon termination, you will retain access to download your health records for a period of 30 days.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">11. Governing Law</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                These Terms of Service shall be governed by and construed in accordance with the laws 
                of the Republic of the Philippines.
              </p>
              
              <p className="mt-3">
                Any disputes arising from these terms shall be resolved through:
              </p>
              <ol className="list-decimal list-inside ml-4 space-y-2">
                <li>Good faith negotiation between the parties</li>
                <li>Mediation, if negotiation fails</li>
                <li>Arbitration or courts of the Philippines, as a last resort</li>
              </ol>
            </div>
          </section>

          {/* Section 12 */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={24} className="text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">12. Changes to Terms</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                We may modify these Terms of Service at any time. We will notify you of material changes by:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Email notification to your registered address</li>
                <li>Prominent notice on our platform</li>
                <li>Requiring re-acceptance upon next login (for major changes)</li>
              </ul>
              
              <p className="mt-3">
                Your continued use of our services after changes constitutes acceptance of the updated terms.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={24} className="text-blue-700" />
              <h2 className="text-2xl font-bold text-slate-900">13. Contact Information</h2>
            </div>
            <div className="space-y-2 text-slate-700 leading-relaxed">
              <p>
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p className="font-semibold text-slate-900">Dr. TJ Malvar, Founder</p>
                <p className="font-medium">Pilgrim Health Virtual Primary Care Clinic</p>
                <p><strong>Email:</strong> pilgrimhealthph@gmail.com</p>
                <p><strong>Mobile:</strong> 0929-804-4770</p>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-600">
          <p className="mb-2">© 2024 We Are Pilgrims. All rights reserved.</p>
          <div className="flex justify-center gap-4">
            <Link to="/privacy" className="text-blue-700 hover:text-blue-900 underline">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/login" className="text-blue-700 hover:text-blue-900 underline">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
